import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { v4 as uuidv4 } from "uuid";
import CustomModal from "components/common/CustomModal";
import CustomSwitch from "components/common/CustomSwitch";
import { TrashIconSvg } from "static/image/common/SvgAssets";
import { theme } from "styles/theme";

export interface TaskMetadataValues {
  task_id: string;
  title: string;
  set_number?: number;
  is_key_activity: boolean;
}

interface TaskMetadataModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialValues?: TaskMetadataValues;
  existingTaskIds: string[];
  // create 모드 세트 번호 기본값 — 현재 존재하는 세트 중 최댓값(미분류 제외). 없으면 빈 값.
  defaultSetNumber?: number;
  // 이미 Key 활동지가 있는 세트 번호 (수정 중인 활동지 자신은 제외).
  // 세트당 Key 는 1개 — 이 목록의 세트에 Key 를 켜면 충돌.
  keyActivitySetNumbers: number[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (values: TaskMetadataValues) => void;
  onRemove?: () => void;
}

const SHORT_ID_LENGTH = 8;
const ID_GENERATION_MAX_RETRIES = 8;

function generateShortTaskId(existingTaskIds: Set<string>): string {
  for (let attempt = 0; attempt < ID_GENERATION_MAX_RETRIES; attempt += 1) {
    const candidate = uuidv4().replace(/-/g, "").slice(0, SHORT_ID_LENGTH);
    if (!existingTaskIds.has(candidate)) {
      return candidate;
    }
  }
  // 8-hex 조합이 모두 충돌하는 일은 사실상 없음. 최후 폴백.
  return uuidv4();
}

const FIELD_HEIGHT = "44px";

const inputWrapperSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  px: "12px",
  height: FIELD_HEIGHT,
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:focus-within": {
    borderColor: theme.vars.palette.Border.Highlight,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
  },
};

const readOnlyWrapperSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  height: FIELD_HEIGHT,
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Background.Level1,
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
};

const plainInputSx = {
  flex: 1,
  fontSize: "13px",
  minHeight: 0,
  py: 0,
  backgroundColor: "transparent",
  border: "none",
  "--Input-minHeight": "0px",
  "--Input-paddingInline": "0px",
  "--Input-focusedHighlight": "transparent",
  "&::before": { display: "none" },
  "&:hover": { backgroundColor: "transparent" },
};

const formLabelSx = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.02em",
  color: theme.vars.palette.Content.Medium,
  mb: "6px",
};

const requiredMarkSx = {
  color: theme.vars.palette.danger[500],
  ml: "2px",
};

const keyConflictTextSx = {
  mt: "6px",
  fontSize: "11px",
  lineHeight: 1.4,
  color: theme.vars.palette.danger[500],
};

export default function TaskMetadataModal({
  open,
  mode,
  initialValues,
  existingTaskIds,
  defaultSetNumber,
  keyActivitySetNumbers,
  isSubmitting = false,
  onClose,
  onSubmit,
  onRemove,
}: TaskMetadataModalProps) {
  const [taskId, setTaskId] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [setNumberText, setSetNumberText] = useState<string>("");
  const [isKeyActivity, setIsKeyActivity] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  useEffect(() => {
    if (!showMenu) return;
    const handlePointerDown = () => setShowMenu(false);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [showMenu]);

  const existingTaskIdSet = useMemo(
    () =>
      new Set(
        existingTaskIds.map((id) => id.trim()).filter((id) => id.length > 0)
      ),
    [existingTaskIds]
  );

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialValues) {
      setTaskId(initialValues.task_id);
      setTitle(initialValues.title);
      setSetNumberText(
        typeof initialValues.set_number === "number"
          ? String(initialValues.set_number)
          : ""
      );
      setIsKeyActivity(initialValues.is_key_activity);
    } else {
      setTaskId(generateShortTaskId(existingTaskIdSet));
      setTitle("");
      setSetNumberText(
        typeof defaultSetNumber === "number" ? String(defaultSetNumber) : ""
      );
      setIsKeyActivity(false);
    }
    setCopied(false);
    setConfirmingDelete(false);
    // existingTaskIdSet 은 의도적으로 의존성 제외 — 모달이 열리는 시점의 값만 사용.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialValues]);

  const titleTrimmed = title.trim();
  const setNumberParsed = useMemo(() => {
    const trimmed = setNumberText.trim();
    if (!trimmed) return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
    return parsed;
  }, [setNumberText]);

  const isValid = titleTrimmed.length > 0 && setNumberParsed !== undefined;

  const isDirty = useMemo(() => {
    if (mode === "create" || !initialValues) return true;
    return (
      titleTrimmed !== initialValues.title ||
      setNumberParsed !== initialValues.set_number ||
      isKeyActivity !== initialValues.is_key_activity
    );
  }, [mode, initialValues, titleTrimmed, setNumberParsed, isKeyActivity]);

  // 세트당 Key 활동지는 1개 — Key 를 켰는데 그 세트에 이미 다른 Key 가 있으면 충돌.
  const keyActivityConflict =
    isKeyActivity &&
    setNumberParsed !== undefined &&
    keyActivitySetNumbers.includes(setNumberParsed);

  const canSubmit = isValid && isDirty && !keyActivityConflict;

  const handleSubmit = () => {
    if (!canSubmit || setNumberParsed === undefined) return;
    onSubmit({
      task_id: taskId,
      title: titleTrimmed,
      set_number: setNumberParsed,
      is_key_activity: isKeyActivity,
    });
  };

  const handleCopyTaskId = async () => {
    if (!taskId) return;
    try {
      await navigator.clipboard.writeText(taskId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 권한 없음 등 — 무시
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box component="span">
            {mode === "create" ? "새 활동지 만들기" : "활동지 메타데이터"}
          </Box>
          {mode === "edit" && onRemove && (
            <Box sx={{ position: "relative" }}>
              <IconButton
                variant="plain"
                size="sm"
                color="neutral"
                disabled={isSubmitting}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowMenu((prev) => !prev);
                }}
                sx={{
                  minHeight: "32px",
                  minWidth: "32px",
                  borderRadius: "8px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  more_vert
                </span>
              </IconButton>
              {showMenu && (
                <Box
                  onPointerDown={(event) => event.stopPropagation()}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    zIndex: 30,
                  }}
                >
                  <List
                    orientation="vertical"
                    sx={{
                      width: "max-content",
                      borderRadius: "12px",
                      border: `1px solid ${theme.vars.palette.Border.SuperWeak}`,
                      backgroundColor: theme.vars.palette.Surface.Forward,
                      boxShadow:
                        "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
                      overflow: "hidden",
                      p: 0,
                      "--List-gap": 0,
                      "--ListItem-paddingY": 0,
                    }}
                  >
                    <ListItem sx={{ p: 0 }}>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              "& svg": { width: "18px", height: "18px" },
                            }}
                          >
                            <TrashIconSvg />
                          </Box>
                        }
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowMenu(false);
                          setConfirmingDelete(true);
                        }}
                        sx={{
                          "--Button-gap": "10px",
                          width: "100%",
                          height: "40px",
                          fontSize: "14px",
                          fontWeight: 500,
                          pl: "16px",
                          pr: "20px",
                          justifyContent: "flex-start",
                          color: theme.vars.palette.Content.Strong,
                          backgroundColor: theme.vars.palette.Surface.Forward,
                        }}
                      >
                        삭제하기
                      </Button>
                    </ListItem>
                  </List>
                </Box>
              )}
            </Box>
          )}
        </Box>
      }
      subtitle={
        mode === "create"
          ? "활동지의 기본 정보를 입력해주세요."
          : "이 활동지의 메타데이터를 수정합니다."
      }
      actions={[
        {
          label: mode === "create" ? "만들기" : "저장",
          onClick: handleSubmit,
          variant: "Solid",
          color: "primary",
          buttonProps: { disabled: !canSubmit, loading: isSubmitting },
        },
        {
          label: "취소",
          onClick: onClose,
          variant: "Plain",
        },
      ]}
    >
      <Stack spacing={"16px"} sx={{ pt: "4px" }}>
        <FormControl sx={{ width: "50%" }}>
          <FormLabel sx={formLabelSx}>활동지 ID</FormLabel>
          <Box sx={readOnlyWrapperSx}>
            <Typography
              level="body-sm"
              sx={{
                flex: 1,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: "13px",
                color: theme.vars.palette.Content.Strong,
                userSelect: "all",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {taskId || "—"}
            </Typography>
            <Tooltip
              title={copied ? "복사됨" : "ID 복사"}
              size="sm"
              variant="solid"
            >
              <IconButton
                variant="plain"
                size="sm"
                onClick={handleCopyTaskId}
                sx={{
                  minHeight: "24px",
                  minWidth: "24px",
                  color: theme.vars.palette.Content.Medium,
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level2,
                    color: theme.vars.palette.Content.Strong,
                  },
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  {copied ? "check" : "content_copy"}
                </span>
              </IconButton>
            </Tooltip>
          </Box>
        </FormControl>

        <FormControl>
          <FormLabel sx={formLabelSx}>
            제목
            <Box component="span" sx={requiredMarkSx}>
              *
            </Box>
          </FormLabel>
          <Box sx={inputWrapperSx}>
            <Input
              variant="plain"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="예: 우울감 알아차리기"
              autoFocus
              sx={plainInputSx}
            />
          </Box>
        </FormControl>

        <Box sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
          <FormControl sx={{ width: "33.333%" }}>
            <FormLabel sx={formLabelSx}>
              세트 번호
              <Box component="span" sx={requiredMarkSx}>
                *
              </Box>
            </FormLabel>
            <Box sx={inputWrapperSx}>
              <Input
                variant="plain"
                type="number"
                value={setNumberText}
                onChange={(event) => setSetNumberText(event.target.value)}
                placeholder="예: 1"
                slotProps={{ input: { min: 1 } }}
                sx={plainInputSx}
              />
            </Box>
          </FormControl>

          <FormControl sx={{ width: "33.333%" }}>
            <FormLabel sx={formLabelSx}>Key 활동지</FormLabel>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                pl: "4px",
                height: FIELD_HEIGHT,
              }}
            >
              <CustomSwitch
                checked={isKeyActivity}
                onChange={(event) => setIsKeyActivity(event.target.checked)}
              />
            </Box>
            {keyActivityConflict && (
              <Typography sx={keyConflictTextSx}>
                이 세트엔 이미 Key 활동지가 있어요
              </Typography>
            )}
          </FormControl>
        </Box>
      </Stack>

      <CustomModal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="활동지를 삭제하시겠어요?"
        subtitle="이 작업은 되돌릴 수 없습니다."
        actions={[
          {
            label: "삭제",
            onClick: () => {
              setConfirmingDelete(false);
              onRemove?.();
            },
            variant: "Solid",
            color: "danger",
            buttonProps: { loading: isSubmitting },
          },
          {
            label: "취소",
            onClick: () => setConfirmingDelete(false),
            variant: "Plain",
          },
        ]}
      />
    </CustomModal>
  );
}
